import { routePaths } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/cn";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { TourTaskAbortReason } from "@/libs/api/types";
import { useTourStop } from "@/libs/tours-detail/hooks/useTourStop";
import { useTranslation } from "react-i18next";

/**
 * TODO: This file contains AI generated Code. The code in this file may not be complete.
 * We defenetly need to refactor this.
 */

export function DeliveryFailedPage() {
  const { t } = useTranslation();

  const { id, stopId, taskId } = useParams<{
    id: string;
    stopId: string;
    taskId: string;
  }>();

  // Todo: create hook
  const abortReasonLabels: Record<TourTaskAbortReason, string> = {
    [TourTaskAbortReason.NOT_REACHED]: t("deliveryFailed.reasons.notReached"),
    [TourTaskAbortReason.UNDERAGED_PERSON]: t(
      "deliveryFailed.reasons.underagedPerson",
    ),
    [TourTaskAbortReason.FORCE_MAJEURE]: t(
      "deliveryFailed.reasons.forceMajeure",
    ),
    [TourTaskAbortReason.DAMAGED]: t("deliveryFailed.reasons.damaged"),
    [TourTaskAbortReason.CUSTOMER_UNKNOWN]: t(
      "deliveryFailed.reasons.customerUnknown",
    ),
    [TourTaskAbortReason.ADDRESS_UNKNOWN]: t(
      "deliveryFailed.reasons.addressUnknown",
    ),
    [TourTaskAbortReason.DELIVERY_DECLINED]: t(
      "deliveryFailed.reasons.deliveryDeclined",
    ),
    [TourTaskAbortReason.BUSINESS_ADDRESS]: t(
      "deliveryFailed.reasons.businessAddress",
    ),
    [TourTaskAbortReason.MISSING]: t("deliveryFailed.reasons.missing"),
    [TourTaskAbortReason.OTHER_REASON]: t("deliveryFailed.reasons.other"),
    [TourTaskAbortReason.UNKNOWN]: t("deliveryFailed.reasons.unknown"),
  };

  const [step, setStep] = useState(0);

  const [note, setNote] = useState("");
  const [selectedAbortReason, setSelectedAbortReason] = useState<string>(
    TourTaskAbortReason.NOT_REACHED,
  );

  const isBulkTask = taskId?.startsWith("bulk");

  const { deliverFailTourStop, currentStop } = useTourStop(stopId as string);

  const currentTask = currentStop?.items.find(
    (item) => item.id === (taskId as string),
  );

  const handleChipClick = (value: string) => {
    setSelectedAbortReason(value);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              {Object.entries(abortReasonLabels).map(([value, label]) => (
                <div
                  key={value}
                  className={cn(
                    "px-4 py-2 rounded-full cursor-pointer bg-gray-200 text-gray-700 select-none",
                    {
                      "bg-primary text-white": selectedAbortReason === value,
                    },
                  )}
                  onClick={() => handleChipClick(value)}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t("deliveryFailed.note.label")}
            </h3>
            <Textarea
              value={note}
              rows={10}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("deliveryFailed.note.placeholder")}
              className="mt-2"
            />
          </div>
        );
    }
  };

  if (!currentStop || !currentTask) return;

  return (
    <div className="h-screen bg-slate-100">
      <div className="container mx-auto max-w-2xl h-full flex flex-col ">
        <div className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
          {step === 1 ? (
            <Link
              to=""
              onClick={() => setStep(0)}
              className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              {t("common.back")}
            </Link>
          ) : (
            <Link
              to={routePaths.createTourStopPath(
                id as string,
                stopId as string,
                currentTask.type,
              )}
              className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              {t("common.back")}
            </Link>
          )}

          <div className="space-y-2">
            <header className="">
              <h3 className="text-xl font-semibold">
                {t("deliveryFailed.header.title")}
              </h3>
            </header>
          </div>
        </div>

        <section className="p-4 ">{renderStep()}</section>

        <div className="bg-white p-4 fixed bottom-0 left-0 right-0 flex gap-4 border-t-2 border-neutral pb-safe">
          {step === 0 ? (
            <Button onClick={() => setStep(1)} className="w-full">
              {t("common.next")}
            </Button>
          ) : (
            <Button
              onClick={() => {
                deliverFailTourStop(
                  currentTask.parentId,
                  currentTask.id,
                  {
                    abortReason: selectedAbortReason,
                    driverNote: note,
                  },
                  isBulkTask,
                );
              }}
              className="w-full"
            >
              {t("common.done")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
